import { useQuery } from '@tanstack/react-query'
import { z } from 'zod'
import { api } from '@/api/client'

const nullableString = z.string().nullable().optional()
const nullableNumber = z.number().nullable().optional()
const rankMovementSchema = z.enum(['up', 'down', 'same', 'new']).nullable()

export interface PouleStandingsResponse {
  poule: {
    id: number
    name: string
    description?: string | null | undefined
  }
  users: PouleStandingUser[]
}

export interface PouleStandingUser {
  user_id: number
  name: string
  profile_image?: string | null | undefined
  total_points: number
  badge_points?: number | undefined
  total_with_badges?: number | undefined
  rank?: number | null | undefined
  previous_rank?: number | null | undefined
  rank_movement?: 'up' | 'down' | 'same' | 'new' | null | undefined
  team?: {
    id: number
    name: string
  } | null | undefined
}

export interface TeamRankingResponse {
  teams: TeamRankingItem[]
}

export interface TeamRankingItem {
  team_id: number
  display_name?: string | null | undefined
  name?: string | null | undefined
  team_display_name?: string | null | undefined
  team_name?: string | null | undefined
  member_count: number
  total_points?: number | null | undefined
  rank?: number | null | undefined
  previous_rank?: number | null | undefined
}

export interface UiRankingUser {
  userId: number
  name: string
  profileImage?: string | null | undefined
  totalPoints: number
  rank?: number | null | undefined
  rankMovement?: 'up' | 'down' | 'same' | 'new' | null | undefined
  teamName?: string | null | undefined
}

const standingUserSchema = z
  .object({
    user_id: z.number(),
    name: z.string(),
    profile_image: nullableString,
    total_points: z.number(),
    badge_points: z.number().optional(),
    total_with_badges: z.number().optional(),
    rank: nullableNumber,
    previous_rank: nullableNumber,
    rank_movement: rankMovementSchema.optional(),
    team: z
      .object({
        id: z.number(),
        name: z.string(),
      })
      .passthrough()
      .nullable()
      .optional(),
  })
  .passthrough()

const standingsResponseSchema = z
  .object({
    poule: z
      .object({
        id: z.number(),
        name: z.string(),
        description: nullableString,
      })
      .passthrough(),
    users: z.array(standingUserSchema).optional().default([]),
  })
  .passthrough()

const teamRankingItemSchema = z
  .object({
    team_id: z.number(),
    display_name: nullableString,
    name: nullableString,
    team_display_name: nullableString,
    team_name: nullableString,
    member_count: z.number(),
    total_points: nullableNumber,
    rank: nullableNumber,
    previous_rank: nullableNumber,
  })
  .passthrough()

const teamRankingResponseSchema = z
  .object({
    teams: z.array(teamRankingItemSchema).optional().default([]),
  })
  .passthrough()

async function fetchStandings(pouleId: number): Promise<PouleStandingsResponse> {
  const response = await api.get<unknown>(`/poules/${pouleId}/standings`)
  const parsed = standingsResponseSchema.safeParse(response)

  if (!parsed.success) {
    console.error('[poules-ranking] Invalid standings payload', parsed.error.flatten())
    throw new Error('Ranking kon niet worden geladen.')
  }

  return {
    poule: parsed.data.poule,
    users: sortStandingUsers(parsed.data.users),
  }
}

async function searchStandings(pouleId: number, query: string): Promise<PouleStandingsResponse> {
  const params = new URLSearchParams({ q: query })
  const response = await api.get<unknown>(
    `/poules/${pouleId}/standings/search?${params.toString()}`,
  )
  const parsed = standingsResponseSchema.safeParse(response)

  if (!parsed.success) {
    console.error('[poules-ranking] Invalid search standings payload', parsed.error.flatten())
    throw new Error('Zoekresultaten konden niet worden geladen.')
  }

  return {
    poule: parsed.data.poule,
    users: sortStandingUsers(parsed.data.users),
  }
}

async function fetchTeamRanking(pouleId: number): Promise<TeamRankingResponse> {
  const response = await api.get<unknown>(`/poules/${pouleId}/teams/ranking`)
  const parsed = teamRankingResponseSchema.safeParse(response)

  if (!parsed.success) {
    console.error('[poules-ranking] Invalid team ranking payload', parsed.error.flatten())
    throw new Error('Team ranking kon niet worden geladen.')
  }

  return {
    teams: sortTeamRanking(parsed.data.teams),
  }
}

export function usePouleStandings(pouleId: number | null, enabled = true) {
  return useQuery({
    queryKey: ['poule-standings', pouleId],
    queryFn: () => fetchStandings(pouleId as number),
    enabled: pouleId != null && enabled,
    staleTime: 30_000,
  })
}

export function usePouleStandingsSearch(
  pouleId: number | null,
  query: string,
  enabled = true,
) {
  return useQuery({
    queryKey: ['poule-standings-search', pouleId, query],
    queryFn: () => searchStandings(pouleId as number, query),
    enabled: pouleId != null && enabled && query.trim().length > 0,
    staleTime: 15_000,
  })
}

export function usePouleTeamRanking(pouleId: number | null, enabled = true) {
  return useQuery({
    queryKey: ['poule-team-ranking', pouleId],
    queryFn: () => fetchTeamRanking(pouleId as number),
    enabled: pouleId != null && enabled,
    staleTime: 30_000,
  })
}

export function toUiRankingUsers(users: PouleStandingUser[]): UiRankingUser[] {
  return users.map((user) => ({
    userId: user.user_id,
    name: user.name,
    profileImage: user.profile_image ?? undefined,
    totalPoints: user.total_points,
    rank: user.rank ?? undefined,
    rankMovement: user.rank_movement ?? undefined,
    teamName: user.team?.name ?? undefined,
  }))
}

function sortStandingUsers(users: PouleStandingUser[]) {
  return [...users].sort((left, right) => {
    const leftRank = left.rank ?? Number.MAX_SAFE_INTEGER
    const rightRank = right.rank ?? Number.MAX_SAFE_INTEGER
    if (leftRank !== rightRank) return leftRank - rightRank
    return left.name.localeCompare(right.name, 'nl')
  })
}

function sortTeamRanking(teams: TeamRankingItem[]) {
  return [...teams].sort((left, right) => {
    const leftRank = left.rank ?? Number.MAX_SAFE_INTEGER
    const rightRank = right.rank ?? Number.MAX_SAFE_INTEGER
    if (leftRank !== rightRank) return leftRank - rightRank
    return getTeamLabel(left).localeCompare(getTeamLabel(right), 'nl')
  })
}

export function getTeamLabel(team: TeamRankingItem) {
  return (
    team.display_name?.trim() ||
    team.team_display_name?.trim() ||
    team.name?.trim() ||
    team.team_name?.trim() ||
    'Onbekend team'
  )
}
